import { useState, useEffect } from 'react'
import styles from '../style.module.scss'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import Joi from 'joi'
import http from '../../../../../shared/http'
import { TApi, TRoutes } from '../../../../../shared/const'
import { schemaOptions } from '../../../../../shared/i18n'
import { canonicalOptionValue, optionsFor } from '../../../../../shared/optionList'
import { joiResolver } from '@hookform/resolvers/joi'
import SelectField from '../../../../../UI/SelectField'
import HygieneTraining from '../../../common/HygieneTraining'
import { isReleaseActive, releaseHint } from '../../../../../shared/revisionRelease'
import TextAreaField from '../../../../../UI/TextAreaField'
import Btn from '../../../../../UI/Btn'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
const schema = Joi.object({
	branch: Joi.string().required(),
	companyDescription: Joi.string().min(50).max(1000).required(),
	offerTypes: Joi.string().required()
})

function CompanyProfile(props) {
	const { id } = useParams()
	const location = useLocation()
	const path = location.pathname

	const { t, i18n } = useTranslation()
	const {
		register,
		trigger,
		control,
		watch,
		setValue,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		}),
		/*
		 * Die gespeicherten Werte auf den Schluessel der Auswahlliste umsetzen
		 * (`canonicalOptionValue`): An aelteren Datensaetzen steht die Branche als
		 * **deutscher** Text ("Nahrungsmittel & Getraenke"), die Auswahlliste
		 * vergleicht aber gegen `value_EN` ("Food & Drinks") - ohne Treffer zeigte
		 * react-select seinen Platzhalter, obwohl ein Wert gespeichert war.
		 * Dasselbe gilt fuer die Angebotsart. Derselbe Befund wie in Anforderung
		 * #77, nachgezogen mit `shared/optionList`.
		 */
		defaultValues: {
			branch: canonicalOptionValue(props.branch, props.companyProfile.branch),
			companyDescription: props.companyProfile.description,
			offerTypes: canonicalOptionValue(props.typeOfOffers, props.companyProfile.offerTypes)
		}
	})
	const [isLoading, setLoading] = useState(false)

	useEffect(() => {
		if (Object.keys(errors).length) trigger()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localStorage.getItem('i18nextLng')])

	/*
	 * Die Stammdaten stehen im Store und koennen erst nach dem ersten Rendern
	 * ankommen - die Startwerte oben sind dann noch nicht umgesetzt. Deshalb hier
	 * noch einmal, sobald die Listen stehen oder das Profil neu geladen wurde.
	 * `setValue` statt `reset`: Ein `reset` wuerde die Beschreibung mitnehmen und
	 * eine noch nicht gespeicherte Eingabe verwerfen.
	 */
	useEffect(() => {
		setValue('branch', canonicalOptionValue(props.branch, props.companyProfile.branch))
		setValue('offerTypes', canonicalOptionValue(props.typeOfOffers, props.companyProfile.offerTypes))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.branch, props.typeOfOffers, props.companyProfile.branch, props.companyProfile.offerTypes])

	const onSubmit = data => {
		setLoading(true)
		const schema = {
			branch: data.branch,
			description: data.companyDescription,
			offerTypes: data.offerTypes
		}
		const updateCompany =
			path === TRoutes.COMPANY
				? http.put(TApi.COMPANY_PUT_PROFILE, schema)
				: http.put(TApi.ADMIN_GET_COMPANY(id), schema)

		updateCompany
			.then(() => {
				toast.success(t('Company profile has been updated successfully.'))
				// Die Ampel am Reiter haengt am geladenen Profil — nach dem Speichern nachziehen.
				props.refreshProfile?.()
			})
			.finally(() => setLoading(false))
	}
	/*
	 * Die Branche ist nach dem Einreichen und nach der Aktivierung gesperrt —
	 * seit #230 auch im Server. Gibt die Verwaltung die Bearbeitung frei, ist
	 * sie für die Dauer der Freigabe wieder offen.
	 */
	const releaseOpen = isReleaseActive(props.companyProfile.revisionRelease)
	const branchLocked = !releaseOpen && (props.companyProfile.isActive || props.companyProfile.isNeedReview)
	const hint = releaseHint(t, props.companyProfile.revisionRelease)

	return (
		<div className={styles.root}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.section}>
					<h1 className={styles.sectionTitle}>{t('Business Activity')}</h1>
					<div className={styles.sectionContent}>
						{hint && (
							<p className={styles.releaseHint} role="status">
								{hint}
							</p>
						)}
						<SelectField
							required
							className={styles.field}
							disabled={isLoading || branchLocked}
							mb={36}
							label={t('branch')}
							control={control}
							name="branch"
							error={errors.branch}
							placeholder={t('Select Branch')}
							options={optionsFor(
								props.branch,
								i18n.language,
								canonicalOptionValue(props.branch, props.companyProfile.branch)
							)}
						/>

						{/*
						 * Lebensmittelhygiene und Allergeninformation (Anforderung #96):
						 * steht unmittelbar unter dem Feld und erscheint nur bei den
						 * dafür hinterlegten Branchen. `watch` statt des gespeicherten
						 * Werts, damit der Hinweis schon beim Auswählen dasteht.
						 */}
						<HygieneTraining
							companyId={props.companyProfile?.id}
							/* In der Schüler:innen-Ansicht der Lehrkraft bleibt alles gelesen —
							   wie die Felder darüber. Widersprechen kann sie auf ihrem
							   eigenen Dashboard, mit ihrem eigenen Namen. */
							readOnly={props.isTeacherStudentView}
							branch={watch('branch')}
							savedBranch={props.companyProfile?.branch}
							onChanged={props.refreshProfile}
						/>

						<div className={styles.flexContainer}>
							<TextAreaField
								required
								classNameLabel={styles.textArea}
								disabled={isLoading}
								height={128}
								label={t('description')}
								placeholder={t('descriptionPlaceholder')}
								error={errors.companyDescription}
								register={register('companyDescription')}
							/>

							{/* <span className={styles.sectionNote}>{t('descriptionNote')}</span> */}
						</div>
						<div className={styles.flexContainer}>
							<SelectField
								required
								className={styles.field}
								mb={20}
								label={t('typeOffers')}
								control={control}
								name="offerTypes"
								error={errors.offerTypes}
								placeholder={t('typeOffers')}
								options={optionsFor(
									props.typeOfOffers,
									i18n.language,
									canonicalOptionValue(props.typeOfOffers, props.companyProfile.offerTypes)
								)}
								disabled={isLoading}
							/>

							{/* <span className={styles.sectionNote}>{t('typeOffersNote')}</span> */}
						</div>

						{!props.isTeacherStudentView && (
							<div className={styles.btnContainer}>
								<Btn
									className={styles.btn}
									loading={isLoading}
									type="submit"
									icon="arrow"
									iconSize={{ w: 10, h: 10 }}
									iconColor="#fff"
								>
									{t('save')}
								</Btn>
							</div>
						)}
					</div>
				</div>
			</form>
		</div>
	)
}

CompanyProfile.propTypes = {
	companyProfile: PropTypes.object,
	refreshProfile: PropTypes.func,
	branch: PropTypes.array.isRequired,
	typeOfOffers: PropTypes.array.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired
}
const mapStateToProps = state => ({
	branch: state.options.branch,
	typeOfOffers: state.options.typeOfOffers
})
export default connect(mapStateToProps, null)(CompanyProfile)
